function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizeDocuments(payload) {
  if (Array.isArray(payload?.documents)) {
    return payload.documents;
  }

  if (payload?.documents && typeof payload.documents === 'object') {
    return Object.values(payload.documents);
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

export const DOCUMENT_SORT_FIELDS = Object.freeze(['created', 'modified', 'name', 'status', 'type', 'expirationDate']);
export const DEFAULT_DOCUMENT_SORT = Object.freeze({
  sortBy: 'created',
  sortOrder: 'desc',
});

function buildProfileName(document) {
  const profile = document?.profile || {};
  const firstName = pickFirst(profile.firstName, profile.first_name);
  const lastName = pickFirst(profile.lastName, profile.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(profile.email, profile.uid) || '-';
}

function buildAssignedToName(document) {
  const assigned = document?.assigned || {};
  const firstName = pickFirst(assigned.firstName, assigned.first_name);
  const lastName = pickFirst(assigned.lastName, assigned.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(assigned.email, assigned.uid) || '-';
}

export function buildDocumentsRequestDto(filters = {}, pagination = {}, sort = {}) {
  const filterBy = [];

  const pushFilter = (field, value) => {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return;
    }

    filterBy.push({ field, filter: normalized });
  };

  pushFilter('name', filters.name);
  pushFilter('status', filters.status);
  pushFilter('type', filters.type);

  if (filters.translationRequired) {
    filterBy.push({ field: 'translationRequired', filter: 'true' });
  }

  const sortBy = DOCUMENT_SORT_FIELDS.includes(sort?.sortBy) ? sort.sortBy : DEFAULT_DOCUMENT_SORT.sortBy;
  const sortOrder = ['asc', 'desc'].includes(sort?.sortOrder) ? sort.sortOrder : DEFAULT_DOCUMENT_SORT.sortOrder;
  const dto = {
    sortBy,
    sortOrder,
    limit: Number.isInteger(pagination?.limit) && pagination.limit > 0 ? pagination.limit : 20,
    offset: Number.isInteger(pagination?.offset) && pagination.offset > 0 ? pagination.offset : 0,
  };

  if (filterBy.length > 0) {
    dto.filterBy = filterBy;
  }

  if (filters.assignedOnly) {
    dto.assignedOnly = true;
  }

  if (Array.isArray(filters.countriesOnly) && filters.countriesOnly.length > 0) {
    dto.countriesOnly = filters.countriesOnly
      .map((item) => String(item || '').trim().toUpperCase())
      .filter(Boolean);
  }

  return dto;
}

export function mapDocumentsResponseToDto(payload) {
  return normalizeDocuments(payload).map((document, index) => ({
    key: String(pickFirst(document.uid, document.id, `${pickFirst(document.name, 'document')}-${index}`)),
    name: pickFirst(document.name) || '-',
    type: pickFirst(document.type) || '-',
    status: pickFirst(document.status) || '-',
    profileId: String(pickFirst(document?.profile?.uid, document?.profile?.id) || '').trim(),
    profileName: buildProfileName(document),
    assignedToId: String(pickFirst(document?.assigned?.uid, document?.assigned?.id) || '').trim(),
    assignedToName: buildAssignedToName(document),
    translation: pickFirst(document.translation) || '-',
    translationRequired: pickFirst(document.translationRequired, document.translation_required, null),
    created: pickFirst(document.created, document.createdAt, document.created_at),
    modified: pickFirst(document.modified, document.updatedAt, document.updated_at),
    expiration: pickFirst(document.expirationDate, document.expiration_date),
  }));
}
